/* eslint-disable @typescript-eslint/no-explicit-any */
import { filterListBasedOnKey } from './CommonUtils';
import type { IAccountMember, IUser, UserUpdatePayload } from '@/common/interfaces/user.interface';
import { MEMBERS_PILL_COLORS } from '@/constants/Home';

interface ISchool {
  id: number;
  for_iterable?: string;
  value: string;
  label: string;
  name?: string;
}

interface IOrg {
  id: number;
  name?: string;
  value: string;
  label: string;
}

/**
 * A method that returns the collegiate details.
 * @param schoolOptions - list of schools
 * @param orgOptions - list of organizations
 * @param collegiateData - Changed data
 * @param getSchool - Callback to fetch a specific school
 * @param getOrganization - Callback to fetch a specific organization
 */
export const getCollegiateDetails = async (
  schoolOptions: ISchool[],
  orgOptions: IOrg[],
  collegiateData: { schoolField: string; organizationField: string },
  getSchool: (params: { page: number; term: string }) => Promise<any>,
  getOrganization: (params: { page: number; term: string }) => Promise<any>
) => {
  let selectedSchool = filterListBasedOnKey(schoolOptions, 'value', collegiateData.schoolField)[0];
  let selectedOrg = filterListBasedOnKey(orgOptions, 'value', collegiateData.organizationField)[0];

  /** Find the school. */
  if (!selectedSchool) {
    const { data: schoolValue } = await getSchool({
      page: 1,
      term: collegiateData.schoolField,
    });

    selectedSchool = schoolValue && schoolValue[0];
  }

  /** Find the organization. */
  if (!selectedOrg) {
    const { data: organizationValue } = await getOrganization({
      page: 1,
      term: collegiateData.organizationField,
    });

    selectedOrg = organizationValue && organizationValue[0];
  }

  return {
    selectedSchool,
    selectedOrg,
  };
};

export const NULLABLE_COLLEGIATE_DATA = {
  organizationName: null,
  schoolId: null,
  schoolName: null,
  schoolNameForIterable: null,
  graduationYear: null,
  position: null,
};

/**
 * Methdo to set the account member information for the id, name, email, and the color object.
 *
 * @param data - Account members list.
 */
export const getAccountMembersPillInfo = (data: IAccountMember[]) => {
  if (data) {
    const accountMemberList: {
      id: number;
      name: string;
      email: string;
      colorPill: {
        bgColor: string;
        color: string;
        borderColor: string;
      };
    }[] = [];

    const activeAccountMembers = data;

    const noOfTimesToMerge =
      activeAccountMembers.length > 9
        ? Math.ceil(activeAccountMembers.length / 9)
        : activeAccountMembers.length;

    const colorPillList = Array.from({ length: noOfTimesToMerge }).flatMap(
      () => MEMBERS_PILL_COLORS
    );

    data.forEach((accountMember, index) => {
      accountMemberList.push({
        id: accountMember.id,
        name: accountMember.full_name,
        email: accountMember.email,
        colorPill: colorPillList[index],
      });
    });

    return accountMemberList;
  }

  return data;
};

/**
 * Method to check if a user's details are updated.
 *
 * @param data - The current data.
 * @param user  - Existing user data.
 */
export const isUserDetailsUpdated = (data: UserUpdatePayload, user: IUser) => {
  const updatedFields = [];

  if (data.email !== user?.email) {
    updatedFields.push('Email');
  }

  if (data.phone !== user?.phone) {
    updatedFields.push('Phone');
  }

  if (data.fullName !== user?.full_name) {
    updatedFields.push('Name');
  }

  if (data.gradYear !== user?.school_year) {
    updatedFields.push('Grad Year');
  }

  if (data.newPassword) {
    updatedFields.push('Password');
  }

  return updatedFields;
};
